import { useState, useEffect } from "react";
import {
   BarChart as ReBarChart, Bar, ResponsiveContainer, YAxis 
} from "recharts";

import { useSelector } from "react-redux";
import { COLORS } from "../../../../theme/colors";
import { Box, makeStyles, Typography, Grid } from "@material-ui/core";

import { Icon } from "../../../../components/base/Icon";
import CarbonIcon from "../../../../assets/images/buildings/Carbon.svg";
import ProfitabilityIcon from "../../../../assets/images/buildings/Profitability.svg";
import OccupancyIcon from "../../../../assets/images/buildings/Occupancy.svg";
import ConnectionIcon from "../../../../assets/images/buildings/Connection.svg";
import RentalRevenueIcon from "../../../../assets/images/building/rentalRevenue.svg";
import PowerTradingRevenueIcon from "../../../../assets/images/building/powerTradingRevenue.svg";
import PowerAssetsRevenuesIcon from "../../../../assets/images/building/powerAssetRevenue.svg";
import BuildingProfitabilityIcon from "../../../../assets/images/building/building-profitability.svg";
import { getData } from "../../../../services/api";

import { ShaDowBox } from "../../../../components/boxes";
import { ActionTimeGroup } from "../../../../components/actionTimeTimeGroup";
import { TooltipContent } from "../../../../components/charts/TooltipContent";
import { Tooltip } from "../../../../components/charts/Tooltip";

export const useStyles = makeStyles((theme) => ({
  title: {
    fontWeight: "bold",
    fontSize: 18,
    lineHeight: "24px",
    textAlign: "center",
    paddingBottom: theme.spacing(1),
  },
  description: {
    color: COLORS.blueShine,
    textAlign: "center",
    fontSize: 21,
    fontWeight: "bold",
    lineHeight: "30px",
  },
  actionButtonsGroup: {
    color: "green",
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  icon: {
    width: 85,
    height: 85,
    borderRadius: "100%",
    background: COLORS.darks[0][6],
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 10px",
  },
}));

export const InAGlanceTab = () => {
  const classes = useStyles();
  const currencySymbol = useSelector((state) => state.auth.user.currency.symbol);
  const [toggleFilter, setToggleFilter] = useState("$/sqft");
  const [items, setItems] = useState([]);

  const { period, startDate, endDate } = useSelector((state) => state.selectedPeriods);
  const ICON = {
    rental_revenue: RentalRevenueIcon,
    vacancy: OccupancyIcon,
    carbon_reduction: CarbonIcon,
    noi_increase: ProfitabilityIcon,
    connected_tenant: ConnectionIcon,
    power_trading_revenue: PowerTradingRevenueIcon,
    power_asset_revenue: PowerAssetsRevenuesIcon,
    building_profitability: BuildingProfitabilityIcon,
  }

  const calculateMaxDomain = (data) => {
    const max = Math.max(...data.map((item) => item.value));
    return max + (max * 0.1);
  }

  const getVacancyDescription = (value) => {
    return <Typography
      component="span"
      style={{ color: COLORS.blueShine }}
      className={classes.description}
    >
      {value}%
    </Typography>
  }

  useEffect(() => {
    getData("praas/v1/buildings/at-a-glance/", (results) => {
      const items = [];
      results.map((result) => {
        return {
          ...result,
          maxDomain: calculateMaxDomain(result.chart_data),
          icon: ICON[result.type],
        }
      }).forEach((result) => {
        switch (result.type) {
          case "rental_revenue":
            items.push({
              ...result,
              description: <Typography className={classes.description}>${result.data.min}-${result.data.max}/sqft</Typography>,
            })
            break;
          case "vacancy":
            items.push({
              ...result,
              description: (
                <Typography className={classes.description} style={{ color: COLORS.darks[0][100] }}>
                  Physical {getVacancyDescription(result.data.physical)}; Economic {getVacancyDescription(result.data.economic)}
                </Typography>
              ),
            })
            break;
          case "carbon_reduction":
            items.push({
              ...result,
              description: <Typography className={classes.description}>2.8 kCO2/sqft</Typography>,
            })
            break;
          case "noi_increase":
            items.push({
              ...result,
              description: <Typography className={classes.description}>+1.5%</Typography>,
            })
            break;
          case "connected_tenant":
            items.push({
              ...result,
              description: (
                <Typography className={classes.description}>
                  {result.data.percent}%{" "}
                  <Typography
                    component="span"
                    style={{ color: COLORS.darks[0][100], fontWeight: "normal", fontSize: 18 }}
                    className={classes.description}
                  >
                    ({result.data.connected}/total {result.data.total})
                  </Typography>
                </Typography>
              ),
            })
            break;
          case "power_trading_revenue":
          case "power_asset_revenue":
            items.push({
              ...result,
              description: <Typography className={classes.description}>${result.data}/sqft</Typography>,
            })
            break;
          case "building_profitability":
            items.push({
              ...result,
              description: <Typography className={classes.description}>{result.data}</Typography>,
            })
            break;
          default:
            break;
        }
      });
      setItems(items);
    });
  }, [period, startDate, endDate]);

  return (
    <Box>
      <Box className={classes.actionButtonsGroup}>
        <ActionTimeGroup
          showToggleFilter={true}
          toggleOptions={[`${currencySymbol}/sqft`, "Total"]}
          toggleFilter={toggleFilter}
          setToggleFilter={setToggleFilter}
          hideIntegrateBtn
        />
      </Box>
      <Grid container spacing={2} style={{ marginTop: 48 }}>
        {items.map((item) => (
          <Grid item xs={12} sm={6} md={3} key={item.id}>
            <ShaDowBox style={{ padding: "54px 30px 40px" }}>
              <Box className={classes.icon}>
                <Icon src={item.icon} style={{ width: 55 }} />
              </Box>
              <Typography className={classes.title}>{item.title}</Typography>
              {item.description}
              <Box style={{ marginTop: 40 }}>
                <ResponsiveContainer width="100%" height={100}>
                  <ReBarChart
                    style={{
                      width: "100%",
                      maxWidth: "300px",
                    }}
                    responsive
                    data={item.chart_data}
                    margin={{
                      top: 20,
                    }}
                  >
                    <YAxis domain={[0, item.maxDomain]} hide />
                    <Tooltip
                      wrapperStyle={{ outline: "none" }}
                      chartData={item.chart_data}
                      content={
                        <TooltipContent
                          chartData={item.chart_data}
                          period={period}
                        />
                      }
                    />
                    <Bar
                      dataKey="value"
                      size={8}
                      fill={COLORS.purple}
                      background={COLORS.darks[0][1]}
                      backgroupColor={COLORS.darks[0][1]}
                    />
                  </ReBarChart>
                </ResponsiveContainer>
              </Box>
            </ShaDowBox>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
